'use client';

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';

const DEFAULT_APP_NAME = 'Diamond CRM';
const LS_CACHE_KEY = 'glanz_app_name_cache';

interface AppNameContextType {
  appName: string;
  setAppName: (name: string) => void;
  refreshAppName: () => Promise<void>;
}

const AppNameContext = createContext<AppNameContextType>({
  appName: DEFAULT_APP_NAME,
  setAppName: () => {},
  refreshAppName: async () => {},
});

export const useAppName = () => useContext(AppNameContext);

export const AppNameProvider = ({ children }: { children: React.ReactNode }) => {
  // Always initialise with DEFAULT_APP_NAME so server and client render the
  // same value on first pass — prevents hydration mismatch.
  const [appName, setAppNameState] = useState<string>(DEFAULT_APP_NAME);

  const refreshAppName = useCallback(async () => {
    try {
      const supabase = createClient();
      const { data, error } = await supabase
        .from('general_settings')
        .select('value')
        .eq('key', 'app.name')
        .single();

      if (!error && data?.value) {
        // value is stored as JSON string e.g. '"Diamond CRM"'
        let name: string = DEFAULT_APP_NAME;
        try {
          const parsed = typeof data.value === 'string' ? JSON.parse(data.value) : data.value;
          if (typeof parsed === 'string' && parsed.trim()) {
            name = parsed.trim();
          }
        } catch {
          if (typeof data.value === 'string' && data.value.trim()) {
            name = data.value.trim();
          }
        }
        setAppNameState(name);
        try {
          localStorage.setItem(LS_CACHE_KEY, name);
        } catch {
          // storage unavailable (private mode) — in-memory state stays
        }
      }
    } catch {
      // Network error — keep cached value
    }
  }, []);

  // On mount (client-only): first apply localStorage cache for instant display,
  // then fetch from DB as the authoritative source.
  useEffect(() => {
    try {
      const cached = localStorage.getItem(LS_CACHE_KEY);
      if (cached && cached.trim()) {
        setAppNameState(cached.trim());
      }
    } catch {
      // storage unavailable — ignore
    }
    refreshAppName();
  }, [refreshAppName]);

  // setAppName is kept for backward compatibility but now only updates
  // local state + cache; the authoritative write goes through
  // GeneralSettingsContent → supabase general_settings upsert.
  const setAppName = (name: string) => {
    const trimmed = name.trim() || DEFAULT_APP_NAME;
    setAppNameState(trimmed);
    try {
      localStorage.setItem(LS_CACHE_KEY, trimmed);
    } catch {
      // storage unavailable — ignore
    }
  };

  return (
    <AppNameContext.Provider value={{ appName, setAppName, refreshAppName }}>
      {children}
    </AppNameContext.Provider>
  );
};
